import { useState, useEffect } from 'react';
import { ConfirmationDialog, Dialog, Button } from '../../ui';
import debounce from 'lodash/debounce';

import { useIdleTimer } from 'react-idle-timer';
import { logSessionInformation } from './sessionLogger';
import { getAppConfig } from '../../config';
import { isTokenExpired } from '../../utils';

const idleTimeoutMinutes = getAppConfig().idleTimeout ?? 10;

function useIsUserActive() {
    const { isIdle } = useIdleTimer({
        events: ['mousemove', 'keydown'],
        timeout: 60_000 * idleTimeoutMinutes,
        onActive(event) {
            logSessionInformation({ event: 'UserIsActive', reason: event?.type });
        },
    });

    return () => !isIdle();
}

const WARNING_TIME_MINUTES = 5;

// TODO: add to app config?
const FORCED_LOGOUT_TIME = idleTimeoutMinutes * 60 * 1000; // idleTimeout from minutes to milliseconds

type TimerAction = 'RESTART' | 'EARLY_LOGOUT' | 'LOGOUT';

export function SessionTimer({
    expiresInMilliseconds,
    refreshUserLogin,
    retrieveTokenExpiration,
    logout,
}: {
    expiresInMilliseconds: number;
    refreshUserLogin(): Promise<void>;
    retrieveTokenExpiration(): void;
    logout(): void;
}) {
    const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
    const [showTimeout, setShowTimeout] = useState(false);

    const isUserActive = useIsUserActive();

    // Handle browser tab waking up from suspension with an expired token
    useEffect(() => {
        function handleVisibilityChange() {
            if (document.visibilityState === 'visible' && isTokenExpired()) {
                logSessionInformation({ event: 'ForcedLogoutTokenExpired' });
                logout();
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [logout]);

    // Forced logout after maximum session duration
    useEffect(() => {
        if (!showTimeout) {
            return;
        }

        const timeoutId = setTimeout(() => {
            logSessionInformation({ event: 'ForcedLogoutTimeout' });
            logout();
        }, FORCED_LOGOUT_TIME);

        // eslint-disable-next-line consistent-return
        return () => {
            clearTimeout(timeoutId);
        };
    }, [logout, showTimeout]);

    const promptBeforeIdleMilliseconds = WARNING_TIME_MINUTES * 60_000;

    const { message } = useIdleTimer({
        events: [],
        promptBeforeIdle:
            promptBeforeIdleMilliseconds < expiresInMilliseconds
                ? promptBeforeIdleMilliseconds
                : expiresInMilliseconds - 1000,
        onPrompt: () => {
            if (isUserActive()) {
                logSessionInformation({ event: 'AutomaticSessionExtension' });
                refreshUserLogin()
                    .then(() => {
                        message('RESTART', true);
                    })
                    .catch(() => {
                        message('LOGOUT', true);
                    });
                return;
            }
            logSessionInformation({ event: 'SessionWarningOpen' });

            setShowTimeoutWarning(true);
        },
        timeout: expiresInMilliseconds,
        onIdle: () => {
            setShowTimeoutWarning(false);
            setShowTimeout(true);
        },
        onMessage: (data: TimerAction, idleTimer) => {
            switch (data) {
                case 'RESTART': {
                    retrieveTokenExpiration();
                    setShowTimeoutWarning(false);

                    idleTimer?.start();
                    break;
                }
                case 'EARLY_LOGOUT': {
                    idleTimer?.reset();
                    logout();
                    break;
                }
                case 'LOGOUT': {
                    logout();
                    break;
                }
                default: {
                    throw new Error('Unexpected Action');
                }
            }
        },
        syncTimers: 200,
        crossTab: true,
    });

    const broadcast = (action: TimerAction, emitOnSelf?: boolean) => message(action, emitOnSelf);

    const handleSessionExtension = debounce(async () => {
        logSessionInformation({ event: 'ManualSessionExtension' });
        setShowTimeoutWarning(false);
        try {
            await refreshUserLogin();
            broadcast('RESTART', true);
        } catch (e) {
            broadcast('LOGOUT', true);
        }
    }, 500);

    const handleEarlyLogout = () => broadcast('EARLY_LOGOUT', true);
    const handleLogout = () => broadcast('LOGOUT', true);

    return (
        <>
            <ConfirmationDialog
                open={showTimeoutWarning}
                disableClickOutside
                title="Session Timeout Warning"
                prompt={`Your user session will expire in ${WARNING_TIME_MINUTES} minute(s). Would you like to continue your user session or logout?`}
                confirm={{
                    buttonTitle: 'Continue',
                    onClick: handleSessionExtension,
                }}
                reject={{
                    buttonTitle: 'Logout',
                    onClick: handleEarlyLogout,
                }}
                color="warn"
            />
            <Dialog
                open={showTimeout}
                title="Session Timeout"
                content="Your session has expired."
                handleOnClose={handleLogout}
                actions={<Button onClick={handleLogout}>Ok</Button>}
            />
        </>
    );
}
